import { GridFilterModel } from '@mui/x-data-grid';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { API_URL, enrichedFetch } from '../../services/api';
import { CustomFile } from '../../Types/file';
import { RootState } from '../store';

export const getUserFiles = createAsyncThunk<
  { data: CustomFile[]; total: number },
  {
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc' | null;
    filterModel?: GridFilterModel;
  } | void,
  { state: RootState }
>('files/getUserFiles', async (params, { getState, rejectWithValue }) => {
  try {
    const { searchQuery } = getState().files;
    const queryParams = new URLSearchParams();

    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.sortField) queryParams.append('sortField', params.sortField);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);
    if (params?.filterModel?.items?.length) {
      queryParams.append('filterModel', JSON.stringify(params.filterModel));
    }
    if (searchQuery !== undefined) queryParams.append('searchQuery', searchQuery.toString());

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = (await enrichedFetch(`${API_URL}/files/user${queryString}`)) as {
      data: CustomFile[];
      total: number;
    };

    return response;
  } catch (error) {
    enqueueSnackbar('Failed to fetch user files', { variant: 'error' });
    return rejectWithValue('Failed to fetch user files');
  }
});

export const getFolderFiles = createAsyncThunk<
  { data: CustomFile[]; total: number },
  {
    folderId?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc' | null;
    filterModel?: GridFilterModel;
  } | void,
  { state: RootState }
>('files/getFolderFiles', async (params, { getState, rejectWithValue }) => {
  try {
    const { searchQuery } = getState().files;
    const queryParams = new URLSearchParams();

    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.sortField) queryParams.append('sortField', params.sortField);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);
    if (params?.filterModel?.items?.length) {
      queryParams.append('filterModel', JSON.stringify(params.filterModel));
    }
    if (searchQuery !== undefined) queryParams.append('searchQuery', searchQuery.toString());

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = (await enrichedFetch(
      `${API_URL}/files/folder/${params?.folderId}${queryString}`
    )) as {
      data: CustomFile[];
      total: number;
    };

    return response;
  } catch (error) {
    enqueueSnackbar('Failed to fetch user files', { variant: 'error' });
    return rejectWithValue('Failed to fetch user files');
  }
});

export const getFile = createAsyncThunk<CustomFile, string, { state: RootState }>(
  'files/getFile',
  async (fileId, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/files/${fileId}`)) as CustomFile;
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch file', { variant: 'error' });
      return rejectWithValue('Failed to fetch files');
    }
  }
);

export const postFile = createAsyncThunk<
  CustomFile,
  { formData: FormData; folderId?: string; promptId?: string }
>('files/postFile', async ({ formData, folderId, promptId }, { rejectWithValue }) => {
  try {
    let url = `${API_URL}/files/upload`;

    const queryParams = new URLSearchParams();
    if (folderId) queryParams.append('folderId', folderId);
    if (promptId) queryParams.append('promptId', promptId);

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = (await enrichedFetch(url, {
      method: 'POST',
      body: formData,
    })) as CustomFile;

    return response;
  } catch {
    enqueueSnackbar('Failed to post file', { variant: 'error' });
    return rejectWithValue('Failed to post file');
  }
});

export const copyFile = createAsyncThunk<
  CustomFile,
  { fileId: string; folderId?: string; promptId?: string }
>('files/copyFile', async ({ fileId, folderId, promptId }, { rejectWithValue }) => {
  try {
    let url = `${API_URL}/files/copy/${fileId}`;
    if (folderId) {
      url += `?folderId=${folderId}`;
    }
    if (promptId) {
      url += `?promptId=${promptId}`;
    }
    const response = (await enrichedFetch(url, {
      method: 'POST',
    })) as CustomFile;
    return response;
  } catch {
    enqueueSnackbar('Failed to copy file', { variant: 'error' });
    return rejectWithValue('Failed to copy file');
  }
});

export const postFilePrivate = createAsyncThunk<CustomFile, FormData>(
  'files/postFilePrivate',
  async (formData, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/files/upload/private`, {
        method: 'POST',
        body: formData,
      })) as CustomFile;
      return response;
    } catch {
      enqueueSnackbar('Failed to post file', { variant: 'error' });
      return rejectWithValue('Failed to post file');
    }
  }
);

export const updateFile = createAsyncThunk<
  void,
  { fileId: string; file: Partial<CustomFile> },
  { state: RootState }
>('files/updateFile', async ({ fileId, file }, { rejectWithValue, dispatch }) => {
  try {
    await enrichedFetch(`${API_URL}/files/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify(file),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    dispatch(getUserFiles());
  } catch {
    enqueueSnackbar('Failed to update file', { variant: 'error' });
    return rejectWithValue('Failed to update file');
  }
});

export const deleteFile = createAsyncThunk<void, string, { state: RootState }>(
  'files/deleteFile',
  async (fileId, { rejectWithValue, dispatch }) => {
    try {
      await enrichedFetch(`${API_URL}/files/${fileId}`, {
        method: 'DELETE',
      });
      dispatch(getUserFiles());
    } catch {
      enqueueSnackbar('Failed to delete file', { variant: 'error' });
      return rejectWithValue('Failed to delete file');
    }
  }
);

export const deleteFilesByPromptId = createAsyncThunk<void, string, { state: RootState }>(
  'files/deleteFilesByPromptId',
  async (promptId, { rejectWithValue, dispatch }) => {
    try {
      await enrichedFetch(`${API_URL}/files/prompt/${promptId}`, {
        method: 'DELETE',
      });
      dispatch(getUserFiles());
    } catch {
      enqueueSnackbar('Failed to delete files', { variant: 'error' });
      return rejectWithValue('Failed to delete files');
    }
  }
);
